import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonComment } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Comment } from "@shared/models/comment"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { assign, partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  planbookId: string
  courseId: string | null
  dateString: string | null
  comment: Comment
  deletedAt: string
}

/**
 * Soft-deletes a comment on a UnifiedCardStack lesson or card by setting its `deletedAt` (only).
 */
export function prepareLessonDeleteComment(payload: Payload): PreparedAction {
  return {
    name: "LESSON_DELETE_COMMENT",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  return [
    Patch.create("comment", payload.comment.id).set("attributes.deletedAt", payload.deletedAt)
      .patch,
  ]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  return [Patch.create("comment", payload.comment.id).set("attributes.deletedAt", null).patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  const models = defaultLessonModels(payload, findFn, userId)
  const context = defaultLessonContext(payload, models)
  const newContext = assign({}, context, {
    objectType: payload.comment.attributes.adverbialObjectType === "card" ? "card" : "lesson",
    commenterId: payload.comment.attributes.commenterId,
  })
  const newEvent: LessonComment = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    primary_object_id: payload.cardStackId,
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "comment",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),
    primary_object_type: "lesson",
    primary_object_specifier:
      payload.comment.attributes.adverbialObjectType === "card" ? "cards" : null,
    adverbial_verb: "removing",
    adverbial_object_type: "comment",
    adverbial_object_specifier: null,
    adverbial_object_id: payload.comment.id,
    adverbial_object_id_and_ancestor_ids: [...models.cardStackIdAndAncestorIds, payload.comment.id],
  }

  return {
    title: "Removed a Comment",
    titleForAnalytics: "Comment - Remove",
    description: `Removed a comment from the ${
      models.cardStack?.attributes.title || "untitled"
    } lesson`,
    context: newContext,
    activity: defaultLessonActivity(payload, models, "removed"),
    newAnalyticsEvents: [newEvent],
  }
}
